import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';
import styles from './styles.module.scss';

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';
import OrderStatusService from '../../../../../services/orders/orderStatus';

// props
import { IOrderStatusResourceShortProps } from '../../../../../props/orders/orderStatus';

// components
import { ActionButton, CommandBar, IColumn, ICommandBarItemProps, PrimaryButton, SearchBox, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import Text from '../../../../typography/text';
import OrderStatusForm from '../form';

interface IOrderStatusListProps {
}

const OrderStatusList: React.FC<IOrderStatusListProps> = (props: IOrderStatusListProps) => {
    const { banner } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [orderStatus, setOrderStatus] = React.useState<IOrderStatusResourceShortProps[]>([]);
    const [activeOrderStatus, setActiveOrderStatus] = React.useState<IOrderStatusResourceShortProps | undefined>();
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    const columns: IColumn[] = [
        {
            key: "uniqueKey",
            fieldName: "key",
            name: "Unique Key",
            minWidth: 120,
            maxWidth: 120,
            onRender: (item: IOrderStatusResourceShortProps) => {
                return <Stack styles={{root: {height: '100%'}}}>
                    <Text>{item.key}</Text>
                </Stack>;
            }
        },
        {
            key: "actions",
            name: "",
            minWidth: 50,
            maxWidth: 50,
            onRender: (item: IOrderStatusResourceShortProps) => {
                return <Stack styles={{root: {height: '100%'}}}>
                    <Stack.Item className={"detailsListActionRow"}>
                        <ActionButton className={'detailsListActionButton'} menuProps={{
                            items: [
                                {key: "update", text: "Update", iconProps: {iconName: "Edit"}, onClick: () => {
                                    setActiveOrderStatus(item);
                                    setActiveSurface('update');
                                }}
                            ]
                        }} />
                    </Stack.Item>
                </Stack>;
            }
        },
        {
            key: "name",
            fieldName: "name",
            name: "Name",
            minWidth: 0,
            onRender: (item: IOrderStatusResourceShortProps) => {
                return <Stack styles={{root: {height: '100%'}}}>
                    <Text>{item.name}</Text>
                </Stack>;
            }
        },
        {
            key: "description",
            fieldName: "description",
            name: "Description",
            minWidth: 200,
            maxWidth: 200,
            isMultiline: true,
            onRender: (item: IOrderStatusResourceShortProps) => {
                return <Stack styles={{root: {height: '100%'}}}>
                    <Text>{item.description}</Text>
                </Stack>;
            }
        },
        {
            key: "backgroundColor",
            name: "Background",
            minWidth: 150,
            maxWidth: 150,
            onRender: (item: IOrderStatusResourceShortProps) => {
                return <Stack styles={{root: {height: '100%'}}}>
                    <Text>{item.backgroundColor}</Text>
                </Stack>;
            }
        },
    ];

    useEffect(() => {
        _onRetrieveOrderStatus();
    }, []);

    const _onRetrieveOrderStatus = async (pageNumber?: number, ) => {
        try {
            if (!pageNumber) {
                setLoaded(false);
            }

            const qs: string[] = [];
            const results = await OrderStatusService.retrieve();
            setOrderStatus(results);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_order_status_error',
                text: 'Failed to retrieve order status. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onKeywordChanged = (value?:string) => {
        setLoaded(true);
        setKeyword(value || "");
    }

    const getCommandBarItems = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        items.push({
            key: "search",
            onRender: () => {
                return <SearchBox placeholder={"Search ..."} onSearch={_onKeywordChanged} />
            }
        });

        farItems.push({
            key: "register",
            text: "Create Order Status",
            iconProps: { iconName: "Add" },
            onRender: () => { 
                return <PrimaryButton text={"Create Order Status"} 
                    iconProps={{ iconName: "Add" }} 
                    onClick={() => {setActiveSurface('create') }} 
                    styles={{ root: { marginLeft: 10 } }} />;
            }
        });

        return { items, farItems };
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        setActiveOrderStatus(undefined);

        if (refresh) {_onRetrieveOrderStatus()}
    }

    return <Stack className={styles.container} tokens={{childrenGap: 20}}>
        <CommandBar
            items={getCommandBarItems().items}
            farItems={getCommandBarItems().farItems}
            styles={{root: {
                padding: 0,
                height: 'unset',
                backgroundColor: 'transparent'
            }}}
            ariaLabel="Use left and right arrow keys to navigate between commands" />
        <Stack className={styles.list}>
            {
                !loaded || (loaded && orderStatus.length > 0) ? (
                    <>
                        <ShimmeredDetailsList
                            setKey="items"
                            items={orderStatus}
                            columns={columns}
                            selectionMode={SelectionMode.none}
                            enableShimmer={!loaded}
                            onShouldVirtualize={() => false}
                            ariaLabelForShimmer="Content is being fetched"
                            ariaLabelForGrid="Item details" />
                    </>
                ) : null
            }
            {loaded && orderStatus.length < 1 ? <Stack styles={{root: {padding: 10}}}>
                <Text>Order status(s) not found</Text>
            </Stack> : null}
        </Stack>
        {activeSurface === 'create' ? <OrderStatusForm onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'update' && activeOrderStatus ? <OrderStatusForm orderStatus={activeOrderStatus} onDismissed={_onSurfaceDismissed} /> : null}
    </Stack>;
};

export default OrderStatusList;
